/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, { useEffect } from "react";

import { usePlayerTranslation } from "../../../../../../shared/context/PlayerTranslationContext";
import AudioIcon from "../../../../../../shared/icons/general/Language.svg";
import VideoQualityIcon from "../../../../../../shared/icons/general/Quality.svg";
import PlaybackRateIcon from "../../../../../../shared/icons/player/Speed.svg";
import ClosedCaptionsIcon from "../../../../../../shared/icons/player/Subtitles.svg";
import {
  DropdownMenuItem,
  DropdownMenuSection,
  useActiveSection,
  useToggleVisibility,
} from "../../../../Base";

import { useAvailableSection } from "./AvailableSectionContext";
import { useAudioSettings } from "./useAudioSettings";
import { usePlaybackRateSettings } from "./usePlaybackRateSettings";
import { useQualitySettings } from "./useQualitySettings";
import { useSubtitleSettings } from "./useSubtitleSettings";

export const MainSection = () => {
  const { visible } = useToggleVisibility();
  const { resetToDefault } = useActiveSection();
  const { isAvailable } = useAvailableSection();
  const { t } = usePlayerTranslation();

  const { activeTrack: activeAudioTrack } = useAudioSettings();
  const { activeTrack: activeVideoTrack } = useQualitySettings();
  const { activeTrack: activeSubtitleTrack } = useSubtitleSettings();
  const { currentPlaybackRate, formatPlaybackRate } = usePlaybackRateSettings();

  useEffect(() => resetToDefault(), [resetToDefault, visible]);

  return (
    <DropdownMenuSection name="settings" withBlockPadding>
      {isAvailable("audio") && (
        <DropdownMenuItem
          navigateToSection="audio"
          sideText={activeAudioTrack?.label}
        >
          <AudioIcon aria-hidden="true" focusable="false" />
          {t("PLAYER_SETTINGS_AUDIO")}
        </DropdownMenuItem>
      )}
      {isAvailable("subtitles") && (
        <DropdownMenuItem
          navigateToSection="subtitles"
          sideText={activeSubtitleTrack?.label}
        >
          <ClosedCaptionsIcon aria-hidden="true" focusable="false" />
          {t("PLAYER_SETTINGS_SUBTITLES")}
        </DropdownMenuItem>
      )}
      {isAvailable("playback-rate") && (
        <DropdownMenuItem
          navigateToSection="playback-rate"
          sideText={formatPlaybackRate(currentPlaybackRate)}
        >
          <PlaybackRateIcon aria-hidden="true" focusable="false" />
          {t("PLAYER_SETTINGS_PLAYBACK_RATE")}
        </DropdownMenuItem>
      )}
      {isAvailable("quality") && (
        <DropdownMenuItem
          navigateToSection="quality"
          sideText={activeVideoTrack?.label}
        >
          <VideoQualityIcon aria-hidden="true" focusable="false" />
          {t("PLAYER_SETTINGS_QUALITY")}
        </DropdownMenuItem>
      )}
    </DropdownMenuSection>
  );
};
